/**
 * Payment Section - Kiosk Settings
 * Credit Buffers, Minimum Cost, Incremental Auth, Transaction Fees, Average kWh Cost
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import { useKioskSettingsStore } from '../../../stores/kiosk-settings-store';
import { SupportRfidFlowEnum } from '../../../types/kiosk-settings-types';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { Switch } from '@/core/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/core/components/ui/tooltip';

function InfoTooltip({ text }: { text: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help shrink-0" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs whitespace-pre-line">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface PaymentSectionProps {
  isOpen: boolean;
  onToggle: () => void;
  hideMaxTransactionFee?: boolean;
}

export const PaymentSection = ({ isOpen, onToggle, hideMaxTransactionFee = false }: PaymentSectionProps) => {
  const { t } = useTranslation('assignEv');
  const { settings, updateField, originalSettings } = useKioskSettingsStore();
  const [averageKwhCostText, setAverageKwhCostText] = useState<string>('');
  const [authRatioText, setAuthRatioText] = useState<string | null>(null);
  const [authRatioBadInput, setAuthRatioBadInput] = useState(false);
  const [acBadInput, setAcBadInput] = useState(false);
  const [dcBadInput, setDcBadInput] = useState(false);
  const [minCostBadInput, setMinCostBadInput] = useState(false);

  useEffect(() => {
    setAuthRatioText(null);
    setAuthRatioBadInput(false);
    setAcBadInput(false);
    setDcBadInput(false);
    setMinCostBadInput(false);
  }, [originalSettings]);

  if (!settings) return null;

  const authRatioDisplayStr = authRatioText ?? (settings.authApprovalMinRatio != null
    ? String(parseFloat((settings.authApprovalMinRatio * 100).toFixed(2)))
    : '');
  const authRatioNumeric = authRatioDisplayStr !== '' ? parseFloat(authRatioDisplayStr) : null;
  const isAuthRatioInvalid = authRatioBadInput
    || (authRatioNumeric !== null && (authRatioNumeric < 0 || authRatioNumeric > 100));

  const maxFeeValue = settings.maxTransactionFeePercentage ?? null;
  const isMaxFeeInvalid = maxFeeValue !== null && (maxFeeValue < 0 || maxFeeValue > 100);

  const acValue = settings.acCreditBuffer ?? null;
  const isAcInvalid = acBadInput || (acValue !== null && (acValue < 0 || acValue > 99.9));
  const dcValue = settings.dcCreditBuffer ?? null;
  const minCostValue = settings.minCostPerMinuteThreshold ?? null;
  const isMinCostInvalid = minCostBadInput || (minCostValue !== null && minCostValue <= 0);

  const kwhCostDisplayValue = averageKwhCostText || String(settings.averageKwhCost ?? '');
  const kwhCostNumeric = kwhCostDisplayValue !== '' ? parseFloat(kwhCostDisplayValue) : null;
  const isKwhCostInvalid = kwhCostNumeric !== null && (kwhCostNumeric < 0.1 || kwhCostNumeric > 99999);
  const isDcInvalid = dcBadInput || (dcValue !== null && (dcValue < 0 || dcValue > 99.9));

  return (
    <div className="rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-100 hover:bg-gray-200 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isOpen ? (
            <ChevronDown className="w-5 h-5 text-gray-700" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-700" />
          )}
          <h3 className="font-bold text-gray-900 text-base">{t('kiosk.paymentSection.title')}</h3>
        </div>
      </button>

      {/* Section Content */}
      {isOpen && (
        <div className="px-4 py-3 bg-white space-y-6">
          {/* Credit Buffers Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* AC Credit Buffer */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Label className="text-xs font-medium text-gray-600">
                  {t('kiosk.paymentSection.fields.acCreditBuffer')}
                </Label>
                <InfoTooltip text="Enter the percentage of the total authorized amount at which the session should stop. For example, if the authorized amount is 200 ILS and you set 20%, the session will stop at 160 ILS." />
              </div>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="99.9"
                value={settings.acCreditBuffer || ''}
                onChange={(e) => {
                  setAcBadInput(e.target.validity.badInput);
                  const value = e.target.value ? parseFloat(e.target.value) : null;
                  updateField('acCreditBuffer', value);
                }}
                className={`bg-white ${isAcInvalid ? 'border-red-500 focus-visible:ring-red-500' : 'border-gray-300'}`}
              />
              {isAcInvalid && <p className="text-xs text-red-500 mt-1">Value must be between 0 and 99.9</p>}
            </div>

            {/* DC Credit Buffer */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Label className="text-xs font-medium text-gray-600">
                  {t('kiosk.paymentSection.fields.dcCreditBuffer')}
                </Label>
                <InfoTooltip text="Enter the percentage of the total authorized amount at which the session should stop. For example, if the authorized amount is 200 ILS and you set 20%, the session will stop at 160 ILS." />
              </div>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="99.9"
                value={settings.dcCreditBuffer || ''}
                onChange={(e) => {
                  setDcBadInput(e.target.validity.badInput);
                  const value = e.target.value ? parseFloat(e.target.value) : null;
                  updateField('dcCreditBuffer', value);
                }}
                className={`bg-white ${isDcInvalid ? 'border-red-500 focus-visible:ring-red-500' : 'border-gray-300'}`}
              />
              {isDcInvalid && <p className="text-xs text-red-500 mt-1">Value must be between 0 and 99.9</p>}
            </div>
          </div>

          {/* Minimum Cost Per Minute | Average kWh Cost */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium text-gray-600 mb-2 block">
                {t('kiosk.paymentSection.fields.minCostPerMinute')}
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={settings.minCostPerMinuteThreshold || ''}
                onChange={(e) => {
                  setMinCostBadInput(e.target.validity.badInput);
                  const value = e.target.value ? parseFloat(e.target.value) : null;
                  updateField('minCostPerMinuteThreshold', value);
                }}
                className={`bg-white ${isMinCostInvalid ? 'border-red-500 focus-visible:ring-red-500' : 'border-gray-300'}`}
              />
              {isMinCostInvalid && <p className="text-xs text-red-500 mt-1">Value must be greater than 0</p>}
            </div>

            {/* Average kWh Cost */}
            <div>
              <Label className="text-xs font-medium text-gray-600 mb-2 block">
                {t('kiosk.paymentSection.fields.averageKwhCost')}
              </Label>
              <Input
                type="text"
                inputMode="decimal"
                value={kwhCostDisplayValue}
                onChange={(e) => {
                  const raw = e.target.value;
                  setAverageKwhCostText(raw);
                  const value = raw ? parseFloat(raw) : null;
                  updateField('averageKwhCost', value);
                }}
                onBlur={() => setAverageKwhCostText('')}
                className={`bg-white ${isKwhCostInvalid ? 'border-red-500 focus-visible:ring-red-500' : 'border-gray-300'}`}
              />
              {isKwhCostInvalid && <p className="text-xs text-red-500 mt-1">Value must be between 0.1 and 99999</p>}
            </div>
          </div>

          {/* Min Auth Approval Ratio | Max Transaction Fee */}
          <div className="grid grid-cols-2 gap-4">
            {/* Min Auth Approval Ratio */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Label className="text-xs font-medium text-gray-600">
                  Min Auth Approval Ratio (%)
                </Label>
                <InfoTooltip text={"Minimum percentage of approved vs. requested pre-auth amount required to start a session.\nRange: 0-100%. Set to 0 to disable. Default: 10%"} />
              </div>
              <Input
                type="number"
                step="1"
                min="0"
                max="100"
                value={authRatioDisplayStr}
                onChange={(e) => {
                  setAuthRatioBadInput(e.target.validity.badInput);
                  setAuthRatioText(e.target.value);
                  const pct = e.target.value !== '' ? parseFloat(e.target.value) : null;
                  updateField('authApprovalMinRatio', pct !== null ? pct / 100 : 0.10);
                }}
                className={`bg-white ${isAuthRatioInvalid ? 'border-red-500 focus-visible:ring-red-500' : 'border-gray-300'}`}
              />
              {isAuthRatioInvalid && (
                <p className="text-xs text-red-500 mt-1">Value must be between 0 and 100</p>
              )}
            </div>

            {/* Max Transaction Fee, operator level only */}
            {!hideMaxTransactionFee && (
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('kiosk.paymentSection.fields.maxTransactionFee')}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={settings.maxTransactionFeePercentage || ''}
                  onChange={(e) => {
                    const value = e.target.value ? parseFloat(e.target.value) : null;
                    updateField('maxTransactionFeePercentage', value);
                  }}
                  className={`bg-white ${isMaxFeeInvalid ? 'border-red-500 focus-visible:ring-red-500' : 'border-gray-300'}`}
                />
                {isMaxFeeInvalid && (
                  <p className="text-xs text-red-500 mt-1">Value must be between 0 and 100</p>
                )}
              </div>
            )}
          </div>

          {/* Incremental Auth Switch */}
          <div className="flex items-center gap-3">
            <Switch
              id="incrementalAuth"
              checked={settings.isSupportedIncrementalAuth || false}
              onCheckedChange={(checked) =>
                updateField('isSupportedIncrementalAuth', checked)
              }
            />
            <Label
              htmlFor="incrementalAuth"
              className="text-sm cursor-pointer"
            >
              {t('kiosk.paymentSection.fields.supportIncrementalAuth')}
            </Label>
          </div>

          {/* Support RFID Card Authorisation Flow */}
          <div className="flex items-center gap-3">
            <Switch
              id="supportRfidFlow"
              checked={settings.supportRfidFlow === SupportRfidFlowEnum.REMOTE_START}
              onCheckedChange={(checked) =>
                updateField('supportRfidFlow', checked ? SupportRfidFlowEnum.REMOTE_START : null)
              }
            />
            <Label htmlFor="supportRfidFlow" className="text-sm cursor-pointer">
              Support RFID Card Authorisation Flow
            </Label>
          </div>
        </div>
      )}
    </div>
  );
};
